import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO, { SITE_URL } from '@/components/SEO';
import { useSustainabilityData } from '@/hooks/useTranslatedData';

const sustainabilitySchema = {
  '@type': 'WebPage',
  name: '可持续承诺 | 膳兜兜 foodgogo',
  description: '膳兜兜foodgogo践行清洁标签、绿色供应链、循环包装与碳中和目标，致力于成为可持续健康食品领域的先行者。',
  url: `${SITE_URL}/sustainability`,
  mainEntity: {
    '@type': 'Organization',
    name: '膳兜兜 foodgogo',
    url: `${SITE_URL}/`,
    description: '膳兜兜foodgogo是依托食品实验室的新锐健康食品品牌，践行清洁标签与可持续发展承诺。',
    knowsAbout: [
      '清洁标签食品',
      '绿色供应链',
      '循环包装',
      '碳中和',
    ],
  },
};

export default function Sustainability() {
  const data = useSustainabilityData();

  return (
    <div className="min-h-screen">
      <SEO
        title="膳兜兜 foodgogo | 把实验室装进兜兜，让健康随身go。"
        description="膳兜兜foodgogo是依托食品实验室的新锐健康食品品牌，将前沿科技融入日常饮食，提供功能性软糖、冻干滋补品、中式草本能量棒等轻养生食品。"
        keywords="膳兜兜,foodgogo,健康食品,功能性软糖,冻干滋补,轻养生,清洁标签"
        schema={sustainabilitySchema}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-24 md:pt-32 pb-16 md:pb-20 overflow-hidden">
          {/* KV Background */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://readdy.ai/api/search-image?query=Abstract%20organic%20botanical%20shapes%20and%20green%20plant%20silhouettes%20on%20soft%20neutral%20background%20sustainability%20and%20nature%20concept%20sage%20green%20and%20warm%20earth%20tones%20delicate%20leaf%20patterns%20and%20circular%20recycle%20motifs%20modern%20eco%20friendly%20brand%20aesthetic%20gentle%20light%20diffusion%20clean%20minimal%20composition%20no%20text&width=1920&height=600&seq=sustain-hero-kv-001&orientation=landscape')`
            }}
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-brand-cream/95 via-brand-cream/88 to-brand-cream/95" />

          <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto text-center">
            <span className="inline-block text-xs font-semibold tracking-wider text-brand-gray uppercase mb-4">
              {data.hero.subtitle}
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-brand-charcoal mb-6 leading-tight">
              {data.hero.title}
            </h1>
            <p className="text-base md:text-lg text-brand-gray leading-relaxed">
              {data.hero.description}
            </p>
          </div>
        </section>

        {/* Commitments */}
        <section className="py-16 md:py-24 bg-white">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div className="space-y-12 md:space-y-16">
              {data.commitments.map((item, index) => (
                <div key={item.title} className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-5 ${
                      index === 0 ? 'bg-brand-mint-light' : index === 1 ? 'bg-brand-green-light' : index === 2 ? 'bg-brand-coral-light' : 'bg-brand-cream'
                    }`}>
                      <i className={`${
                        index === 0 ? 'ri-file-list-3-line' : index === 1 ? 'ri-plant-line' : index === 2 ? 'ri-recycle-line' : 'ri-earth-line'
                      } text-2xl ${
                        index === 0 ? 'text-brand-mint-dark' : index === 1 ? 'text-brand-green' : index === 2 ? 'text-brand-coral' : 'text-brand-charcoal'
                      }`}></i>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-brand-charcoal mb-4">
                      {item.title}
                    </h2>
                    <p className="text-base text-brand-gray leading-relaxed mb-6">
                      {item.description}
                    </p>
                  </div>
                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <div className="grid grid-cols-2 gap-4">
                      {item.stats.map((stat) => (
                        <div key={stat.label} className={`rounded-2xl p-5 md:p-6 text-center ${
                          index === 0 ? 'bg-brand-mint-light' : index === 1 ? 'bg-brand-green-light' : index === 2 ? 'bg-brand-coral-light' : 'bg-brand-cream'
                        }`}>
                          <span className="block text-3xl md:text-4xl font-bold text-brand-charcoal mb-1">
                            {stat.value}
                          </span>
                          <span className="text-xs text-brand-gray">{stat.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section className="py-16 md:py-24 bg-brand-sand">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-brand-charcoal mb-3">
              {data.certifications.title}
            </h2>
            <p className="text-base text-brand-gray mb-10">
              {data.certifications.subtitle}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
              {data.certifications.list.map((cert) => (
                <div key={cert.name} className="bg-white rounded-2xl p-5 md:p-6">
                  <div className="w-10 h-10 rounded-full bg-brand-cream flex items-center justify-center mx-auto mb-3">
                    <i className="ri-shield-check-line text-brand-mint-dark text-lg"></i>
                  </div>
                  <span className="block text-sm font-semibold text-brand-charcoal mb-1">
                    {cert.name}
                  </span>
                  <span className="block text-xs text-brand-gray">{cert.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Report CTA */}
        <section className="py-16 md:py-24 bg-brand-charcoal">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-3xl mx-auto text-center">
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-6">
              <i className="ri-file-text-line text-white text-2xl"></i>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              {data.report.title}
            </h2>
            <p className="text-base text-white/60 leading-relaxed mb-8">
              {data.report.description}
            </p>
            <button className="inline-flex items-center gap-2 px-8 py-3 bg-brand-mint-dark text-white text-sm font-semibold rounded-full hover:bg-brand-mint transition-colors whitespace-nowrap">
              <i className="ri-download-line text-sm"></i>
              {data.report.cta}
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}